import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig, useConfigLoadError } from '../hooks/useConfig';
import { useWriteGate } from '../lib/writeGate';
import { useFocusField } from '../hooks/useFocusField';
import { useProfileContext } from '../hooks/useProfileContext';
import ConfigPageHeader from '../components/ConfigPageHeader';
import ConfigField from '../components/ConfigField';
import AutoTextarea from '../components/AutoTextarea';
import type { StageDesignConfig, StageRiser, TransportType } from '../types';
import { createEmptyStageRiser, createDefaultStageDesignConfig } from '../types';
import { useLayoutOrder } from '../hooks/useLayoutOrder';
import { sortByLayoutOrder } from '../lib/layoutOrder';
import { listStagePlots } from '../api/stageplots';
import type { StagePlot, StageElement } from '../shared/types/stageplot';
import { riserItems, type RiserConfig } from '../components/StagePlotEditor/riserConfig';
import PageLoadError from '../components/PageLoadError';

/** Format a cm dimension as a metres string, e.g. 200 -> "2m", 40 -> "0.4m". */
function metersStr(cm: number): string {
  const m = cm / 100;
  return `${Number.isInteger(m) ? m : m.toFixed(2)}m`;
}

/**
 * Render a stage plot's dimensions as a human-readable "Requested stage
 * dimensions" string, e.g. width 8, height 6, unit 'm' -> "8m × 6m depth".
 * Only non-zero axes are included; returns '' when no usable dimension exists.
 */
function formatStageDims(d?: { width?: number; height?: number; unit?: string }): string {
  if (!d) return '';
  const w = typeof d.width === 'number' && d.width > 0 ? d.width : 0;
  const h = typeof d.height === 'number' && d.height > 0 ? d.height : 0;
  const unit = d.unit === 'ft' ? 'ft' : 'm';
  if (w && h) return `${w}${unit} × ${h}${unit} depth`;
  if (w) return `${w}${unit} wide`;
  if (h) return `${h}${unit} depth`;
  return '';
}

/** Human position label derived from the riser's on-stage coordinates. */
function stagePositionFor(el: StageElement, stage?: { width: number; height: number }): string {
  if (!stage || !stage.width || !stage.height) return '';
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const h = cx < stage.width * 0.33 ? 'left' : cx > stage.width * 0.66 ? 'right' : 'centre';
  const v = cy < stage.height * 0.33 ? 'back' : cy > stage.height * 0.66 ? 'front' : 'centre';
  return `${h} ${v}`.trim();
}

/**
 * Convert a riser StageElement (iconId 'risers', RiserConfig) into a StageRiser
 * display shape so risers drawn in the stage plot editor can be surfaced
 * read-only in the Stage Design "Risers" section (single source of truth = the
 * stage plot; no writes to rider_config, so no double counting).
 */
function riserElementToDisplay(el: StageElement, stage?: { width: number; height: number }) {
  const cfg = (el.config ?? {}) as unknown as RiserConfig;
  // Report the REAL block dimensions from the riser config (RiserConfig
  // width/depth/height in cm → m), so Stage Design shows exactly what was
  // entered in the riser editor (e.g. 3m × 2m), not the padded total
  // footprint that used to surface as an arbitrary 2.08m × 1.08m.
  const items = riserItems(cfg);
  const first = items[0] ?? { width: 100, depth: 200, height: 40 };
  const quantity = typeof cfg?.count === 'number' && cfg.count > 0 ? cfg.count : 1;
  const baseName = el.label && el.label.trim() && el.label !== 'Risers' ? el.label : 'Riser';
  return {
    id: el.id,
    name: baseName,
    quantity,
    width: metersStr(first.width),
    depth: metersStr(first.depth),
    height: metersStr(first.height),
    position: stagePositionFor(el, stage) || '—',
  };
}

/** Stable dedup key so the same riser drawn in several plots appears once. */
function riserDedupKey(r: ReturnType<typeof riserElementToDisplay>): string {
  return `${r.name}|${r.width}|${r.depth}|${r.height}|${r.position}|${r.quantity}`;
}

export default function ConfigStageDesignPage() {
  const { config, updateStageDesign, reload } = useConfig();
  // Lot 3a — the rider_config could not be read: the page would render EMPTY
  // fields as if the band had nothing typed. See PageLoadError.tsx.
  const configLoad = useConfigLoadError();

  // Capability-aware write gate (lot A2-CAP, 2026-09-25). Every write of this
  // page persists through `useConfig` -> PUT /api/bands/:id/rider/config, the
  // rider CONTENT: a globally `read-only` account keeps it ONLY through
  // `content.rider` (conditional pre-guard mount, server/index.js). NEVER the
  // blanket `canWrite` here — it would freeze the very crew member the
  // capability is for.
  const writeGate = useWriteGate();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const { activeProfileId } = useProfileContext();
  const { t } = useTranslation();
  const [transport, setTransport] = useState<TransportType>('van');
  // Risers drawn in the band's stage plots, surfaced read-only in the Risers
  // section (single source of truth = the stage plot; no double counting).
  const [plotRisers, setPlotRisers] = useState<
    ReturnType<typeof riserElementToDisplay>[]
  >([]);
  // Human-readable dimensions string derived from the band's stage plot(s),
  // used to auto-fill the "Requested stage dimensions" field once. '' when no
  // plot with usable dimensions exists.
  const [plotDims, setPlotDims] = useState('');
  // Per-band guard so the auto-fill happens exactly once and never overwrites
  // a value the user has typed or cleared themselves.
  const stageDimsAutoFilled = useRef<Record<string, boolean>>({});
  useFocusField(true);

  // Load risers present in the band's stage plots so they appear in the Stage
  // Design "Risers" section alongside the editable structured list.
  useEffect(() => {
    if (!activeProfileId) {
      setPlotRisers([]);
      return;
    }
    let cancelled = false;
    listStagePlots({ bandId: activeProfileId, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        const seen = new Set<string>();
        const items: ReturnType<typeof riserElementToDisplay>[] = [];
        let foundDims = '';
        for (const p of res.items) {
          const data = p.data as StagePlot;
          if (!data) continue;
          // First plot with usable dimensions drives the auto-fill.
          if (!foundDims) foundDims = formatStageDims(data.stageDimensions);
          if (!Array.isArray(data.elements)) continue;
          for (const el of data.elements) {
            if (el.iconId !== 'risers') continue;
            const d = riserElementToDisplay(el, data.stageDimensions);
            const key = riserDedupKey(d);
            if (seen.has(key)) continue;
            seen.add(key);
            items.push(d);
          }
        }
        setPlotRisers(items);
        setPlotDims(foundDims);
      })
      .catch(() => {
        if (!cancelled) setPlotRisers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProfileId]);
  // Merge risers drawn in the band's stage plots into the editable structured
  // risers list (rider_config.stageDesign.<transport>.risers) so they are
  // EDITABLE in Stage Design and appear in the PDF. Each plot riser is added
  // once (dedup by name); once present it is edited directly in this list and
  // never re-added on subsequent loads. Runs again on transport change so
  // every transport the user views picks up the plot risers.
  useEffect(() => {
    if (plotRisers.length === 0) return;
    const cfg = config.stageDesign[transport] || createDefaultStageDesignConfig();
    const current = Array.isArray(cfg.risers) ? cfg.risers : [];
    const existing = new Set(current.map((r) => (r as StageRiser)?.name).filter(Boolean));
    const toAdd = plotRisers.filter((r) => !existing.has(r.name));
    if (toAdd.length === 0) return;
    const merged: StageRiser[] = [
      ...current,
      ...toAdd.map((r) => ({
        id: r.id,
        name: r.name,
        width: r.width,
        depth: r.depth,
        height: r.height,
        quantity: String(r.quantity ?? 1),
        position: r.position === '—' ? '' : r.position,
      })),
    ];
    updateStageDesign({ [transport]: { ...cfg, risers: merged } } as any);
    // config intentionally excluded: we only need it to compute the merge once,
    // and including it would re-trigger the write on every edit (loop risk).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotRisers, transport]);
  // Reorder the Stage Design sections to mirror the Rider Layout's
  // `stage-design.*` order.
  const layoutOrder = useLayoutOrder('stage-design');

  const cfg = config.stageDesign[transport] || createDefaultStageDesignConfig();
  const risers = Array.isArray(cfg.risers) ? cfg.risers : [];

  const handleChange = (key: keyof StageDesignConfig, value: any) => {
    updateStageDesign({ [transport]: { ...cfg, [key]: value } } as any);
  };

  // Auto-fill "Requested stage dimensions" from the band's stage plot
  // (single source of truth for dimensions), exactly once per band, and never
  // overwrite a value the user has typed. Depends only on [plotDims, transport]
  // (NOT on config) so the resulting write can't re-trigger this effect — no
  // autosave loop.
  useEffect(() => {
    if (!activeProfileId || !plotDims) return;
    if (stageDimsAutoFilled.current[activeProfileId]) return;
    // Mark as handled first (even when we skip) so we never touch it again.
    stageDimsAutoFilled.current[activeProfileId] = true;
    const current = (cfg.stageDimensions ?? '').trim();
    if (current) return; // user already entered a value — leave it alone.
    handleChange('stageDimensions', plotDims);
    // config deliberately excluded: we only read it once at fill time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotDims, transport]);

  const updateRiser = (id: string, patch: Partial<StageRiser>) => {
    handleChange('risers', risers.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const addRiser = () => {
    handleChange('risers', [...risers, createEmptyStageRiser()]);
  };
  const removeRiser = (id: string) => {
    handleChange('risers', risers.filter((r) => r.id !== id));
  };

  const populateAll = async (src: TransportType) => {
    const srcCfg = config.stageDesign[src];
    if (!(await showConfirm(t('configStageDesign.populateAllConfirm', { src })))) return;
    updateStageDesign({ [transport]: { ...srcCfg } } as any);
  };

  const populateField = (key: keyof StageDesignConfig) => async (src: TransportType) => {
    const srcVal = config.stageDesign[src][key];
    if (JSON.stringify(srcVal) === JSON.stringify(cfg[key])) return;
    if (await showConfirm(`Overwrite "${key}" with the value from ${src}?`)) {
      handleChange(key, srcVal);
    }
  };

  const riserInput = (riser: StageRiser, key: keyof StageRiser, placeholder: string) => (
    <input disabled={!canEditContent}
      className="form-input"
      style={{ width: '100%', minWidth: 0 }}
      value={(riser[key] as string) ?? ''}
      placeholder={placeholder}
      onChange={(e) => updateRiser(riser.id, { [key]: e.target.value } as any)}
    />
  );

  // Each Rider Layout field (`stage-design.*`) is its own reorderable block,
  // so reordering in the Layout Editor is reflected here. Blocks whose id is
  // not in the layout keep their default relative order (via sortByLayoutOrder).
  const blocks = [
    {
      id: 'stage-design.dimensions',
      render: () => (
        <div className="form-section" data-field-id="stage-design.dimensions">
          <h2>{t('configStageDesign.stageDimensions')}</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0 0 8px' }}>
            {t('configStageDesign.stageDimensionsHint')}
          </p>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.stageDimensionsLabel')} current={transport} fieldId="stage-design.dimensions" exampleText="8m × 6m depth, 6m height" onUseExample={(t) => handleChange('stageDimensions', t)} onPopulateFrom={populateField('stageDimensions')}>
            <input disabled={!canEditContent}
              className="form-input"
              value={cfg.stageDimensions}
              onChange={(e) => handleChange('stageDimensions', e.target.value)}
              placeholder="e.g. 8m × 6m depth, 6m height"
            />
          </ConfigField>
          {plotDims && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '6px 0 0' }}>
              {t('configStageDesign.stageDimensionsAutoHint', { dims: plotDims })}
            </p>
          )}
        </div>
      ),
    },
    {
      id: 'stage-design.backdrop',
      render: () => (
        <div className="form-section" data-field-id="stage-design.backdrop">
          <h2>{t('configStageDesign.backdrop')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.backdropLabel')} current={transport} fieldId="stage-design.backdrop" exampleText="Black backdrop, full-width, band logo optional" onUseExample={(t) => handleChange('backdrop', t)} onPopulateFrom={populateField('backdrop')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.backdrop}
              onChange={(e) => handleChange('backdrop', e.target.value)}
              placeholder="e.g. Black backdrop, full-width, band logo optional"
              rows={3}
            />
          </ConfigField>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.backdropDimsLabel')} current={transport} fieldId="stage-design.backdrop-dimensions" exampleText="6m × 3m, pipe at 5m" onUseExample={(t) => handleChange('backdropDimensions', t)} onPopulateFrom={populateField('backdropDimensions')}>
            <input disabled={!canEditContent}
              className="form-input"
              value={cfg.backdropDimensions}
              onChange={(e) => handleChange('backdropDimensions', e.target.value)}
              placeholder="e.g. 6m × 3m, pipe at 5m"
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'stage-design.risers',
      render: () => (
        <div className="form-section" data-field-id="stage-design.risers">
          <h2>{t('configStageDesign.risers')}</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0 0 8px' }}>
            {t('configStageDesign.risersHint')}
          </p>
          {risers.length === 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{t('configStageDesign.noRisers')}</p>
          )}
          <div className="backline-list">
            {risers.length > 0 && (
              <div
                className="backline-row"
                style={{ gridTemplateColumns: 'minmax(0,1.1fr) 70px 70px 70px 58px minmax(0,1.3fr) 32px', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}
                aria-hidden="true"
              >
                <span>{t('configStageDesign.riserColName')}</span>
                <span>{t('configStageDesign.riserColWidth')}</span>
                <span>{t('configStageDesign.riserColDepth')}</span>
                <span>{t('configStageDesign.riserColHeight')}</span>
                <span>{t('configStageDesign.riserColQuantity')}</span>
                <span>{t('configStageDesign.riserColPosition')}</span>
                <span />
              </div>
            )}
            {risers.map((riser) => (
              <div key={riser.id} className="backline-row" style={{ gridTemplateColumns: 'minmax(0,1.1fr) 70px 70px 70px 58px minmax(0,1.3fr) 32px' }}>
                {riserInput(riser, 'name', 'e.g. Drum riser')}
                {riserInput(riser, 'width', 'e.g. 2m')}
                {riserInput(riser, 'depth', 'e.g. 1.5m')}
                {riserInput(riser, 'height', 'e.g. 0.4m')}
                {riserInput(riser, 'quantity', 'e.g. 2')}
                {riserInput(riser, 'position', 'e.g. centre back')}
                <button disabled={!canEditContent}
                  type="button"
                  aria-label={t('configStageDesign.removeRiser', { name: riser.name || 'riser' })}
                  className="icon-btn"
                  onClick={() => removeRiser(riser.id)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 16 }}
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
          <button disabled={!canEditContent} type="button" className="btn btn-secondary btn-small" onClick={addRiser} style={{ marginTop: 8 }}>
            {t('configStageDesign.addRiser')}
          </button>
        </div>
      ),
    },
    {
      id: 'stage-design.rug',
      render: () => (
        <div className="form-section" data-field-id="stage-design.rug">
          <h2>{t('configStageDesign.rug')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.rugLabel')} current={transport} fieldId="stage-design.rug" exampleText="Black rug 4m × 2m in front of drum riser" onUseExample={(t) => handleChange('rug', t)} onPopulateFrom={populateField('rug')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.rug}
              onChange={(e) => handleChange('rug', e.target.value)}
              placeholder="e.g. Black rug 4m × 2m in front of drum riser"
              rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'stage-design.accessories',
      render: () => (
        <div className="form-section" data-field-id="stage-design.accessories">
          <h2>{t('configStageDesign.accessories')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.accessories')} current={transport} fieldId="stage-design.accessories" exampleText="Gaffer tape, power strips, spare stands" onUseExample={(t) => handleChange('accessories', t)} onPopulateFrom={populateField('accessories')}>
            <input disabled={!canEditContent}
              className="form-input"
              value={cfg.accessories}
              onChange={(e) => handleChange('accessories', e.target.value)}
              placeholder="e.g. Gaffer tape, power strips, spare stands"
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'stage-design.chairs',
      render: () => (
        <div className="form-section" data-field-id="stage-design.chairs">
          <h2>{t('configStageDesign.chairs')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.chairs')} current={transport} fieldId="stage-design.chairs" exampleText="6 chairs in the wings" onUseExample={(t) => handleChange('chairs', t)} onPopulateFrom={populateField('chairs')}>
            <input disabled={!canEditContent}
              className="form-input"
              value={cfg.chairs}
              onChange={(e) => handleChange('chairs', e.target.value)}
              placeholder="e.g. 6 chairs in the wings"
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'stage-design.tools',
      render: () => (
        <div className="form-section" data-field-id="stage-design.tools">
          <h2>{t('configStageDesign.tools')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.tools')} current={transport} fieldId="stage-design.tools" exampleText="Spare cables, basic tool kit" onUseExample={(t) => handleChange('tools', t)} onPopulateFrom={populateField('tools')}>
            <input disabled={!canEditContent}
              className="form-input"
              value={cfg.tools}
              onChange={(e) => handleChange('tools', e.target.value)}
              placeholder="e.g. Spare cables, basic tool kit"
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'stage-design.notes',
      render: () => (
        <div className="form-section" data-field-id="stage-design.notes">
          <h2>{t('configStageDesign.notes')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configStageDesign.notesLabel')} current={transport} fieldId="stage-design.notes" exampleText="Open to changes, confirm at get-in" onUseExample={(t) => handleChange('notes', t)} onPopulateFrom={populateField('notes')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="e.g. Open to changes, confirm at get-in"
              rows={3}
            />
          </ConfigField>
        </div>
      ),
    },
  ];

  const sortedBlocks = sortByLayoutOrder(blocks, layoutOrder, (b) => b.id);


  // Lot 3a: the rider_config could not be read → empty fields would read as
  // "this band typed nothing". Show the shared state instead; the writes stay
  // gated by `useConfig` skipSave, so a retry cannot wipe anything.
  if (configLoad.failed) {
    return (
      <PageLoadError
        title={t('configStageDesign.title')}
        detail={configLoad.message}
        onRetry={() => void reload().catch(() => undefined)}
      />
    );
  }

  return (
    <div className="page config-page" data-config-page="stage-design" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configStageDesign.title"
        title={t('configStageDesign.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />

      {readOnlyNote && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        {sortedBlocks.map((b) => b.render())}
      </div>
    </div>
  );
}
